import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Mail, LogOut, CalendarDays, MapPin, Car, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PortalBookingCard from "@/components/PortalBookingCard";

export default function MyBookings() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(undefined);
  const [bookings, setBookings] = useState([]);
  const [addons, setAddons] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: me }, { data: list }, { data: ad }] = await Promise.all([api.get("/portal/me"), api.get("/portal/bookings"), api.get("/addons")]);
    setSession(me);
    setBookings(list);
    setAddons(ad);
  };

  useEffect(() => {
    const token = search.get("token");
    const boot = async () => {
      try {
        if (token) {
          await api.post("/portal/exchange", { token });
          setSearch({}, { replace: true });
        }
        await load();
      } catch (e) {
        setSession(null);
        if (token) setError(e?.response?.data?.detail || "That link is invalid or expired — request a new one.");
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/portal/request-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send link");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/portal/logout");
    setSession(null);
    setBookings([]);
    navigate("/my-bookings", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-14 pb-28 md:pb-14" data-testid="portal-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Customer portal</div>
            <h1 className="font-display mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100">My bookings</h1>
          </div>
          {session && (
            <button onClick={logout} data-testid="portal-logout-button" className="cj-btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-2">
              <LogOut className="h-3.5 w-3.5" /> {session.email}
            </button>
          )}
        </div>

        {session === undefined && <div className="mt-10 flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Checking your session…</div>}

        {session === null && (
          <div className="cj-card p-6 sm:p-8 mt-8 max-w-lg" data-testid="portal-login-card">
            <div className="h-11 w-11 rounded-xl bg-[#ff6b00]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#ff6b00]" /></div>
            <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100 mt-4">Sign in with your email</h2>
            <p className="text-sm text-slate-400 mt-1">We'll email you a one-time link — no password needed. Reschedule or add extras up to 24 h before your detail.</p>
            {sent ? (
              <div className="mt-5 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 text-sm text-emerald-200" data-testid="portal-link-sent">
                Check your inbox — if we have bookings for <span className="font-mono">{email}</span>, your sign-in link is on its way (valid 30 min).
              </div>
            ) : (
              <form onSubmit={requestLink} className="mt-5 flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="portal-email-input"
                  className="flex-1 bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#ff6b00]"
                />
                <button type="submit" disabled={busy} data-testid="portal-request-link-button" className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a link"}
                </button>
              </form>
            )}
            {error && <div className="mt-3 text-sm text-red-400 font-mono uppercase tracking-wider" data-testid="portal-error">{error}</div>}
          </div>
        )}

        {session && (
          <div className="mt-8 grid gap-5" data-testid="portal-bookings-list">
            {bookings.length === 0 && <div className="cj-card p-6 text-slate-400 text-sm">No bookings found for this email yet.</div>}
            {bookings.map((b) => (
              <PortalBookingCard key={b.id} booking={b} addons={addons} onChanged={load} />
            ))}
          </div>
        )}

        {session && (
          <div className="mt-8 text-xs text-slate-500 flex flex-wrap gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Changes close 24 h before</span>
            <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> Inside 24 h? Call or email CJ</span>
            <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" /> Extras are added to your balance on the day</span>
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Mobile — we come to you</span>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
